import { AppDialog, SearchableSelect } from "@/components/common";
import { OrgTreeNodeBreadcrumb } from "@/components/common/OrgTreeNodeBreadcrumb";
import { Button } from "@/components/ui/button";
import { useTran } from "@/hooks";
import { Building2 } from "lucide-react";
import { Users } from "lucide-react";
import { ListChecks } from "lucide-react";
import { formatMonth } from "../../utils/period-constants";

const AssignMasterDataDialog = ({
  open,
  onClose,
  onSubmit,
  year,
  organizationMode,
  setOrganizationMode,
  organizationTree,
  selectedOrganizations,
  setSelectedOrganizations,
  reportingYearOrganizationIds,
  selectedReportingYear,
  organizationLoading,
  saving,
  generalOnly = false,
}) => {
  const { t } = useTran();
  const allOrganizations = generalOnly || organizationMode === "all";

  return (
    <AppDialog
      open={open}
      onOpenChange={onClose}
      // size="xl" or "lg" depending on your preference for width
      size="lg"
      header={
        <div className="flex items-center gap-2">
          <ListChecks className="h-6 w-6 text-blue-900" />
          <span className="text-lg font-semibold">
            {t("Assign Selected Metrics")}
          </span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onClose(false)}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              !year ||
              (!allOrganizations &&
                organizationMode === "specific" &&
                selectedOrganizations.length === 0)
            }
          >
            {t("Assign")}
          </Button>
        </div>
      }
    >
      {/* Current Inside Design - Kept Exactly as provided */}
      <div className="py-4 space-y-4">
        {/* Year */}
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-foreground whitespace-nowrap py-1">
            {t("Reporting Year")}
            {/* {t("Reporting Year")} <span className="text-red-500">*</span> */}
          </p>

          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
            {selectedReportingYear ? (
              <>
                <span>{selectedReportingYear.startYear}</span>
                {" ("}
                <span>
                  {formatMonth(selectedReportingYear.startMonth)}
                </span>{" "}
                <span>{selectedReportingYear.startYear}</span>
                <span className="mx-2 text-muted-foreground">-</span>
                <span>{formatMonth(selectedReportingYear.endMonth)}</span>{" "}
                <span>{selectedReportingYear.endYear}</span>
                {")"}
              </>
            ) : (
              <span className="text-muted-foreground">
                {t("No reporting year selected")}
              </span>
            )}
          </div>
        </div>

        {/* Organization Scope */}
        <div>
          <p className="text-xs font-medium text-foreground py-1">
            {t("Organization Scope")}
            {generalOnly && (
              <span className="ml-2 text-muted-foreground font-normal">
                ({t("General disclosures are assigned to Parent Company only")})
              </span>
            )}
          </p>

          {generalOnly ? (
            <p className="text-sm font-semibold text-primary flex items-center gap-1.5">
              <Building2 className="h-4 w-4" />
              {organizationTree?.[0]?.name || t("Parent Company")}
            </p>
          ) : (
            <div className="w-fit rounded-full border border-border p-1">
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant={organizationMode === "all" ? "secondary" : "ghost"}
                  className="h-8 text-xs rounded-full flex items-center gap-1 px-3"
                  disabled={!selectedReportingYear?.isAllOrganization}
                  onClick={() => {
                    setOrganizationMode("all");
                    setSelectedOrganizations([]);
                  }}
                >
                  <Building2 className="h-3.5 w-3.5" />
                  {t("All Organizations")}
                </Button>

                <Button
                  type="button"
                  variant={
                    organizationMode === "specific" ? "secondary" : "ghost"
                  }
                  className="h-8 text-xs rounded-full flex items-center gap-1 px-3"
                  onClick={() => setOrganizationMode("specific")}
                >
                  <Users className="h-3.5 w-3.5" />
                  {t("Specific Organization")}
                </Button>
              </div>
            </div>
          )}
          {!generalOnly && (
            <span className="ml-2 text-muted-foreground text-xs">
              (
              {`${selectedReportingYear?.isAllOrganization ? "All" : reportingYearOrganizationIds.length} organizations are assigned to selected reporting year`}
              )
            </span>
          )}
        </div>

        {/* Organization Select */}
        {organizationMode === "specific" && !generalOnly && (
          <div>
            <p className="text-xs font-medium text-foreground p-1">
              {t("Organization")} <span className="text-red-500">*</span>
            </p>
            <OrgTreeNodeBreadcrumb
              includeParentCompany={true}
              tree={organizationTree}
              value={selectedOrganizations}
              onChange={setSelectedOrganizations}
              placeholder={t("Select organizations")}
              disabled={organizationLoading || saving}
              allowOrgs={reportingYearOrganizationIds}
            />
          </div>
        )}
      </div>
    </AppDialog>
  );
};

export default AssignMasterDataDialog;
